import { z } from 'zod';

import { ApiError } from './ApiError';
import { createRequestId } from './requestId';

export type Fetcher = <T>(path: string, init?: RequestInit) => Promise<T>;

export type FetcherOptions = {
  baseUrl: string;
  /** Injection points for tests. */
  fetchImpl?: typeof fetch;
  requestId?: () => string;
};

/**
 * The API's CSRF marker: every POST, PUT, PATCH and DELETE carries `X-CSRF-Token: 1`. The value is public and fixed; a
 * cross-site form can't set a custom header, and the API also checks the request's Origin. A missing marker is a 403
 * `CSRF_INVALID`, which nothing retries: the fetcher always sends it.
 */
export const CSRF_HEADER = 'X-CSRF-Token';
export const CSRF_HEADER_VALUE = '1';

const PDF = 'application/pdf';

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

const errorBodySchema = z.object({
  code: z.string().optional(),
  message: z.string().optional(),
  requestId: z.string().optional(),
});

const parseErrorBody = async (response: Response): Promise<z.infer<typeof errorBodySchema> | undefined> => {
  let json: unknown;
  try {
    json = await response.json();
  } catch {
    return undefined;
  }
  const parsed = errorBodySchema.safeParse(json);
  return parsed.success ? parsed.data : undefined;
};

/** One request with the client's cookies, request id, CSRF marker and errors, returning the successful Response. */
type Request = (path: string, init?: RequestInit) => Promise<Response>;

/**
 * Session cookie auth (`credentials: 'include'`), a request id on every call for log correlation, the CSRF marker on
 * mutations, and a typed ApiError for anything that isn't 2xx. `Accept` is JSON unless the caller sets its own.
 */
const createRequest = ({ baseUrl, fetchImpl = fetch, requestId = createRequestId }: FetcherOptions): Request => {
  return async (path, init = {}) => {
    const method = (init.method ?? 'GET').toUpperCase();
    const id = requestId();
    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
      headers.set('Accept', 'application/json');
    }
    headers.set('X-Request-Id', id);
    if (MUTATING_METHODS.has(method)) {
      headers.set(CSRF_HEADER, CSRF_HEADER_VALUE);
    }

    const response = await fetchImpl(`${baseUrl}${path}`, { ...init, method, headers, credentials: 'include' });
    if (!response.ok) {
      const body = await parseErrorBody(response);
      throw new ApiError({
        status: response.status,
        code: body?.code ?? `http_${String(response.status)}`,
        message: body?.message ?? (response.statusText || `Request failed with status ${String(response.status)}`),
        requestId: body?.requestId ?? response.headers.get('X-Request-Id') ?? id,
        body,
      });
    }
    return response;
  };
};

/**
 * The one HTTP client for JSON: every request goes through the shared request handling above, and a successful
 * response is parsed as JSON. orval's generated hooks call this through apps/<app>/src/api/mutator.ts.
 */
export const createFetcher = (options: FetcherOptions): Fetcher => {
  const request = createRequest(options);
  return async <T>(path: string, init?: RequestInit): Promise<T> => {
    const response = await request(path, init);
    if (response.status === 204) {
      return undefined as T;
    }
    return (await response.json()) as T;
  };
};

export type BlobFetcher = (path: string) => Promise<Blob>;

/**
 * Fetches a PDF as a Blob, for `DocumentViewer` to frame as a `blob:` URL. A blob document has the portal's origin, so
 * only bytes the API labeled `application/pdf` become one: any other Content-Type throws `unexpected_content_type`.
 */
export const createBlobFetcher = (options: FetcherOptions): BlobFetcher => {
  const request = createRequest(options);
  return async (path) => {
    const response = await request(path, { headers: { Accept: PDF } });
    const type = response.headers.get('Content-Type') ?? '';
    if (type.split(';')[0]?.trim().toLowerCase() !== PDF) {
      throw new ApiError({
        status: response.status,
        code: 'unexpected_content_type',
        message: `Expected ${PDF}, got ${type || 'no Content-Type'}.`,
        requestId: response.headers.get('X-Request-Id') ?? undefined,
        body: undefined,
      });
    }
    return new Blob([await response.arrayBuffer()], { type: PDF });
  };
};
